<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import CounterpartyPickerModal from '../components/CounterpartyPickerModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, toIsoDate } from '../utils/format'
import type {
  CurrencyOption,
  Option,
  PurchaseOrderOption,
} from '../types/finance'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const {
  getSupplierInvoice,
  createSupplierInvoice,
  updateSupplierInvoice,
  listCurrencies,
  listAllAgentsByRole,
  listPurchaseOrders,
} = useFinanceApi()

const invoiceId = computed(() => (route.params.id ? String(route.params.id) : ''))
const isEdit = computed(() => invoiceId.value !== '')

const permissions = computed(() => auth.user?.permissions ?? [])
const canReadPurchaseOrders = computed(() => permissions.value.includes('purchase_orders.view'))

const currencies = ref<CurrencyOption[]>([])
const departments = ref<Option[]>([])
const warehouses = ref<Option[]>([])
const purchaseOrders = ref<PurchaseOrderOption[]>([])
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const formError = ref('')
const counterpartyPickerOpen = ref(false)
const counterpartyName = ref('')
const purchaseOrdersUnavailable = ref(false)

const form = ref({
  supplier_agent_id: '' as string | number,
  purchase_order_id: '' as string | number,
  invoice_number: '',
  invoice_date: toIsoDate(),
  due_date: toIsoDate(),
  currency_code: '',
  exchange_rate: 1,
  invoice_amount: 0,
  department_agent_id: '' as string | number,
  warehouse_agent_id: '' as string | number,
  notes: '',
})

const validationError = computed(() => {
  if (!form.value.supplier_agent_id) {
    return t('finance.common.requiredField', { field: t('finance.payables.supplier') })
  }
  if (!form.value.invoice_number.trim()) {
    return t('finance.common.requiredField', { field: t('finance.payables.invoiceNumber') })
  }
  if (!form.value.invoice_date) {
    return t('finance.common.requiredField', { field: t('finance.payables.invoiceDate') })
  }
  if (!form.value.due_date) {
    return t('finance.common.requiredField', { field: t('finance.common.dueDate') })
  }
  if (form.value.due_date < form.value.invoice_date) {
    return t('finance.payables.dueBeforeInvoice')
  }
  if (!form.value.currency_code) {
    return t('finance.common.requiredField', { field: t('finance.common.currency') })
  }
  if (Number(form.value.invoice_amount) <= 0) {
    return t('finance.common.requiredField', { field: t('finance.common.amount') })
  }
  return ''
})

/** Counterparty picked in the modal: reload the purchase orders that may match. */
async function onSupplierSelected(supplier: Option) {
  counterpartyPickerOpen.value = false
  counterpartyName.value = supplier.name
  form.value.supplier_agent_id = supplier.id
  form.value.purchase_order_id = ''
  await loadPurchaseOrders()
}

/**
 * Purchase orders must match the supplier and currency, so the list is
 * reloaded whenever either changes (`purchase_orders.view` required).
 */
async function loadPurchaseOrders() {
  purchaseOrders.value = []
  if (!canReadPurchaseOrders.value || !form.value.supplier_agent_id) return
  try {
    purchaseOrders.value = await listPurchaseOrders({
      supplier_agent_id: form.value.supplier_agent_id,
      currency_code: form.value.currency_code,
    })
    purchaseOrdersUnavailable.value = false
  } catch {
    purchaseOrdersUnavailable.value = true
    purchaseOrders.value = []
  }
}

function purchaseOrderLabel(order: PurchaseOrderOption): string {
  const amount = formatMoney(order.total_amount, order.currency_code)
  return `#${order.id} — ${amount} (${order.status})`
}

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    const [currencyList, departmentList, warehouseList] = await Promise.all([
      listCurrencies(),
      listAllAgentsByRole('department'),
      listAllAgentsByRole('warehouse'),
    ])
    currencies.value = currencyList
    departments.value = departmentList
    warehouses.value = warehouseList

    if (!isEdit.value) {
      form.value.currency_code =
        currencyList.find((currency) => currency.is_base === 1)?.code || currencyList[0]?.code || ''
      loading.value = false
      return
    }

    const invoice = await getSupplierInvoice(invoiceId.value)
    counterpartyName.value = invoice.supplier_name ?? ''
    form.value = {
      supplier_agent_id: invoice.supplier_agent_id,
      purchase_order_id: invoice.purchase_order_id ?? '',
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date,
      due_date: invoice.due_date,
      currency_code: invoice.currency_code,
      exchange_rate: Number(invoice.exchange_rate ?? 1),
      invoice_amount: Number(invoice.invoice_amount ?? 0),
      department_agent_id: invoice.department_agent_id ?? '',
      warehouse_agent_id: invoice.warehouse_agent_id ?? '',
      notes: invoice.notes ?? '',
    }
    await loadPurchaseOrders()
  } catch (cause) {
    loadError.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (validationError.value) {
    formError.value = validationError.value
    return
  }
  saving.value = true
  formError.value = ''
  try {
    const payload = {
      supplier_agent_id: form.value.supplier_agent_id,
      purchase_order_id: form.value.purchase_order_id || undefined,
      invoice_number: form.value.invoice_number.trim(),
      invoice_date: form.value.invoice_date,
      due_date: form.value.due_date,
      currency_code: form.value.currency_code,
      exchange_rate: Number(form.value.exchange_rate) || 1,
      invoice_amount: Number(form.value.invoice_amount),
      department_agent_id: form.value.department_agent_id || undefined,
      warehouse_agent_id: form.value.warehouse_agent_id || undefined,
      notes: form.value.notes.trim() || undefined,
    }
    const invoice = isEdit.value
      ? await updateSupplierInvoice(invoiceId.value, payload)
      : await createSupplierInvoice(payload)
    await router.push(`/payables/invoices/${invoice.id}`)
  } catch (cause) {
    formError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

watch(
  () => form.value.currency_code,
  () => {
    // A purchase order is bound to its currency, so a change invalidates the link.
    if (!loading.value && form.value.purchase_order_id) form.value.purchase_order_id = ''
    void loadPurchaseOrders()
  },
)

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/payables/invoices">
      ← {{ t('finance.payables.supplierInvoicesTitle') }}
    </RouterLink>
    <h2>{{ isEdit ? t('finance.payables.editInvoice') : t('finance.payables.newInvoice') }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="loadError" class="state-message error">
    <p>{{ loadError }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <form v-else class="form-card" @submit.prevent="submit">
    <h3>{{ t('finance.payables.supplierInvoicesTitle') }}</h3>

    <div class="form-grid">
      <div class="field">
        <label for="supplier-invoice-supplier">{{ t('finance.payables.supplier') }}</label>
        <button
          id="supplier-invoice-supplier"
          class="field-button"
          type="button"
          @click="counterpartyPickerOpen = true"
        >
          <span v-if="counterpartyName">{{ counterpartyName }}</span>
          <span v-else class="placeholder">{{ t('finance.picker.agentTitle') }}</span>
          <span aria-hidden="true">⌕</span>
        </button>
      </div>

      <div class="field">
        <label for="supplier-invoice-number">{{ t('finance.payables.invoiceNumber') }}</label>
        <input
          id="supplier-invoice-number"
          v-model="form.invoice_number"
          type="text"
          autocomplete="off"
        >
        <small>{{ t('finance.payables.numberHint') }}</small>
      </div>

      <div class="field">
        <label for="supplier-invoice-date">{{ t('finance.payables.invoiceDate') }}</label>
        <input id="supplier-invoice-date" v-model="form.invoice_date" type="date">
      </div>

      <div class="field">
        <label for="supplier-invoice-due">{{ t('finance.common.dueDate') }}</label>
        <input id="supplier-invoice-due" v-model="form.due_date" type="date">
      </div>

      <div class="field">
        <label for="supplier-invoice-currency">{{ t('finance.common.currency') }}</label>
        <select id="supplier-invoice-currency" v-model="form.currency_code">
          <option value="">{{ t('finance.common.allCurrencies') }}</option>
          <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
            {{ currency.code }} — {{ currency.name }}
          </option>
        </select>
      </div>

      <div class="field">
        <label for="supplier-invoice-rate">{{ t('finance.common.exchangeRate') }}</label>
        <input
          id="supplier-invoice-rate"
          v-model.number="form.exchange_rate"
          type="number"
          step="0.000001"
        >
      </div>

      <div class="field">
        <label for="supplier-invoice-amount">{{ t('finance.common.amount') }}</label>
        <input
          id="supplier-invoice-amount"
          v-model.number="form.invoice_amount"
          type="number"
          step="0.01"
        >
      </div>

      <div v-if="canReadPurchaseOrders" class="field">
        <label for="supplier-invoice-po">{{ t('finance.payables.purchaseOrder') }}</label>
        <select
          id="supplier-invoice-po"
          v-model="form.purchase_order_id"
          :disabled="!form.supplier_agent_id || purchaseOrders.length === 0"
        >
          <option value="">{{ t('finance.common.none') }}</option>
          <option v-for="order in purchaseOrders" :key="order.id" :value="order.id">
            {{ purchaseOrderLabel(order) }}
          </option>
        </select>
        <small v-if="purchaseOrdersUnavailable">{{ t('finance.payables.purchaseOrdersUnavailable') }}</small>
        <small v-else-if="counterpartyName && purchaseOrders.length === 0">
          {{ t('finance.payables.noPurchaseOrders') }}
        </small>
      </div>

      <div class="field">
        <label for="supplier-invoice-department">{{ t('finance.payables.department') }}</label>
        <select id="supplier-invoice-department" v-model="form.department_agent_id">
          <option value="">{{ t('finance.common.none') }}</option>
          <option v-for="department in departments" :key="department.id" :value="department.id">
            {{ department.name }}
          </option>
        </select>
      </div>

      <div class="field">
        <label for="supplier-invoice-warehouse">{{ t('finance.payables.warehouse') }}</label>
        <select id="supplier-invoice-warehouse" v-model="form.warehouse_agent_id">
          <option value="">{{ t('finance.common.none') }}</option>
          <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
            {{ warehouse.name }}
          </option>
        </select>
      </div>

      <div class="field field-wide">
        <label for="supplier-invoice-notes">
          {{ t('finance.common.notes') }} ({{ t('finance.common.optional') }})
        </label>
        <textarea id="supplier-invoice-notes" v-model="form.notes" />
      </div>
    </div>

    <p v-if="isEdit" class="empty-inline">{{ t('finance.payables.statusHint') }}</p>
    <p v-if="formError" class="error-inline">{{ formError }}</p>

    <div class="form-actions">
      <button class="btn-primary" type="submit" :disabled="saving">
        {{ saving ? t('finance.common.saving') : t('finance.common.save') }}
      </button>
      <RouterLink class="btn-secondary" to="/payables/invoices">
        {{ t('finance.common.cancel') }}
      </RouterLink>
    </div>

    <CounterpartyPickerModal
      :open="counterpartyPickerOpen"
      role="supplier"
      :selected-id="form.supplier_agent_id || null"
      @close="counterpartyPickerOpen = false"
      @select="onSupplierSelected"
    />
  </form>
</template>
